import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter
} from "@/components/ui/dialog";
import {
  Popover, PopoverContent, PopoverTrigger
} from "@/components/ui/popover";
import { Plus, Search, Check } from "lucide-react";
import { toast } from "sonner";

function OrgPicker({ value, onChange }) {
  const [orgs, setOrgs] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  useEffect(() => { api.get("/organisaties").then(r => setOrgs(r.data)); }, []);
  const filtered = orgs.filter(o => o.naam.toLowerCase().includes(q.toLowerCase()));
  const sel = orgs.find(o => o.id === value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" className="w-full justify-start" data-testid="org-picker">
          {sel ? sel.naam : "Selecteer organisatie…"}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[320px] p-0">
        <div className="p-2 border-b border-border">
          <Input autoFocus placeholder="Zoek…" value={q} onChange={(e)=>setQ(e.target.value)} className="h-9" />
        </div>
        <div className="max-h-64 overflow-y-auto">
          {filtered.map(o => (
            <button key={o.id} onClick={()=>{onChange(o.id); setOpen(false);}}
                    className="w-full text-left px-3 py-2 hover:bg-secondary text-sm flex justify-between">
              <span>{o.naam}</span>
              {o.id === value && <Check className="h-4 w-4 text-primary" />}
            </button>
          ))}
          {filtered.length === 0 && <div className="p-4 text-sm text-muted-foreground">Geen resultaten.</div>}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function NewActivityDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ organisatie_id: "", type: "taak", onderwerp: "", omschrijving: "", status: "open" });
  const submit = async () => {
    if (!f.organisatie_id) { toast.error("Selecteer organisatie"); return; }
    if (!f.onderwerp.trim()) { toast.error("Onderwerp verplicht"); return; }
    const { data } = await api.post("/activiteiten", f);
    toast.success("Activiteit gelogd"); setOpen(false);
    setF({ organisatie_id: "", type: "taak", onderwerp: "", omschrijving: "", status: "open" });
    onCreated(data);
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="add-activity-btn"><Plus className="h-4 w-4 mr-2" />Activiteit</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Activiteit loggen</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Organisatie</Label><OrgPicker value={f.organisatie_id} onChange={(v)=>setF({...f,organisatie_id:v})} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Type</Label>
              <Select value={f.type} onValueChange={(v)=>setF({...f,type:v})}>
                <SelectTrigger data-testid="quick-act-type"><SelectValue /></SelectTrigger>
                <SelectContent>{["mail","telefoon","gesprek","taak","notitie"].map(t=><SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Status</Label>
              <Select value={f.status} onValueChange={(v)=>setF({...f,status:v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{["open","afgerond"].map(s=><SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div><Label>Onderwerp</Label><Input data-testid="quick-act-onderwerp" value={f.onderwerp} onChange={(e)=>setF({...f,onderwerp:e.target.value})} /></div>
          <div><Label>Omschrijving</Label><Textarea value={f.omschrijving} onChange={(e)=>setF({...f,omschrijving:e.target.value})} /></div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={()=>setOpen(false)}>Annuleren</Button>
          <Button onClick={submit} data-testid="quick-act-submit">Opslaan</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Activiteiten() {
  const [params] = useSearchParams();
  const [items, setItems] = useState([]);
  const [orgs, setOrgs] = useState({});
  const [q, setQ] = useState("");
  const [type, setType] = useState("alle");
  const [status, setStatus] = useState(params.get("status") || "alle");
  const [datumVan, setDatumVan] = useState("");
  const [datumTot, setDatumTot] = useState("");

  const load = async () => {
    const [{ data: a }, { data: o }] = await Promise.all([
      api.get("/activiteiten", { params: { q, type, status, datum_van: datumVan || undefined, datum_tot: datumTot || undefined }}),
      api.get("/organisaties"),
    ]);
    setItems(a); setOrgs(Object.fromEntries(o.map(x => [x.id, x.naam])));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [type, status, datumVan, datumTot]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q]);

  const toggle = async (a) => {
    const ns = a.status === "open" ? "afgerond" : "open";
    const { data } = await api.patch(`/activiteiten/${a.id}`, { status: ns });
    setItems((x) => x.map(i => i.id === a.id ? data : i));
  };

  return (
    <div className="space-y-6" data-testid="activities-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-sm text-muted-foreground">Werk</div>
          <h1 className="font-display text-4xl font-medium tracking-tight mt-1">Activiteiten</h1>
        </div>
        <NewActivityDialog onCreated={(a)=>setItems(x=>[a,...x])} />
      </div>

      <div className="flex gap-3 flex-wrap items-center bg-card border border-border rounded-xl p-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Zoek onderwerp…" className="pl-9" value={q} onChange={(e)=>setQ(e.target.value)} data-testid="acts-search" />
        </div>
        <Select value={type} onValueChange={setType}>
          <SelectTrigger className="w-[150px]" data-testid="acts-type-filter"><SelectValue /></SelectTrigger>
          <SelectContent>{["alle","mail","telefoon","gesprek","taak","notitie"].map(t=><SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-[130px]" data-testid="acts-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>{["alle","open","afgerond"].map(s=><SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
        <Input type="date" className="w-[160px]" value={datumVan?.slice(0,10)||""} onChange={(e)=>setDatumVan(e.target.value)} />
        <Input type="date" className="w-[160px]" value={datumTot?.slice(0,10)||""} onChange={(e)=>setDatumTot(e.target.value)} />
      </div>

      <div className="bg-card border border-border rounded-xl divide-y divide-border">
        {items.map(a => (
          <div key={a.id} className="p-4 flex items-start justify-between gap-4" data-testid={`act-${a.id}`}>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="capitalize">{a.type}</Badge>
                <span className="font-medium truncate">{a.onderwerp}</span>
              </div>
              <Link to={`/organisaties/${a.organisatie_id}`} className="text-sm text-muted-foreground hover:text-foreground hover:underline">
                {orgs[a.organisatie_id] || "—"}
              </Link>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <Button size="sm" variant={a.status==="open"?"default":"outline"} onClick={()=>toggle(a)} className="capitalize">
                {a.status}
              </Button>
              <span className="text-xs text-muted-foreground whitespace-nowrap">{fmtDateTime(a.datum)}</span>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="p-10 text-center text-muted-foreground text-sm">Geen activiteiten.</div>}
      </div>
    </div>
  );
}
